/**
 * 구독 폼 위젯 — 블록 내부에서 메시지/버튼명/버튼색상 인라인 편집
 * config.defaultTextColor, config.defaultBackgroundColor: Writer 기본 색상
 * 공통 스타일: shared/style.scss (.ejs-widget)
 *
 * layout: "vertical" | "inline" | "hero"
 * form_type: "modal" | "input"
 */
import { stopPropagationOnEditorJsControlKeys } from '@/editorjs/utils';
import { createColorPresetDropdown } from '@/editorjs/tools/shared/color-presets';

export default class SubscribeFormTool {
  static get toolbox() {
    return {
      title: "구독 폼",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z" /><path d="M3 7l9 6l9 -6" /></svg>'
    };
  }

  static get isReadOnlySupported() { return true; }

  constructor({ data, config, api, readOnly }) {
    this.api = api;
    this.config = config || {};
    this.readOnly = readOnly;

    const defaultBtnTextColor = this.config.defaultTextColor || "#FFFFFF";
    const defaultBtnBgColor = this.config.defaultBackgroundColor || "#000000";

    this.data = Object.assign({
      layout: "vertical",
      form_type: "modal",
      message: "뉴스레터를 받아보세요",
      title: "",
      description: "",
      button_text: "구독하기",
      button_text_color: defaultBtnTextColor,
      button_background_color: defaultBtnBgColor,
    }, data || {});
    this.wrapper = null;
    this.editing = false;
  }

  render() {
    this.wrapper = document.createElement("div");
    this._renderView();

    if (!this.readOnly) {
      this.wrapper.addEventListener("click", () => {
        if (!this.editing) this._renderEdit();
      });
      this._handleClickOutside = (e) => {
        if (this.editing && !this.wrapper.contains(e.target)) this._renderView();
      };
      document.addEventListener("click", this._handleClickOutside, true);
    }
    return this.wrapper;
  }

  destroy() {
    if (this._handleClickOutside) document.removeEventListener("click", this._handleClickOutside, true);
  }

  _isDark() {
    return document.documentElement.classList.contains('dark');
  }

  _renderView() {
    this.editing = false;
    const { layout, form_type, message, title, description, button_text, button_text_color, button_background_color } = this.data;
    this.wrapper.className = "";
    this.wrapper.style.cssText = "cursor: pointer; padding: 24px 16px; margin: 16px 0;";

    const btnHtml = `<span style="display: inline-block; padding: 8px 20px; background: ${this._escape(button_background_color)}; color: ${this._escape(button_text_color)}; border-radius: 6px; font-size: 14px; font-weight: 500;">${this._escape(button_text)}</span>`;

    const inputHtml = `<span style="display: block; padding: 8px 12px; border: 1px solid ${this._isDark() ? '#4b5563' : '#e2e8f0'}; border-radius: 6px; font-size: 14px; background: transparent; color: ${this._isDark() ? '#9ca3af' : '#9ca3af'}; flex: 1; min-width: 160px; text-align: left;">이메일 주소</span>`;

    const hintHtml = !this.readOnly ? `<div style="display: flex; align-items: center; justify-content: center; gap: 6px; margin: 8px 0 0 0;">
        <span class="ejs-widget__hint">클릭하여 편집</span>
        <span style="position: relative; display: inline-flex;" class="subscribe-form-tooltip-wrap">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round" class="ejs-widget__hint" style="cursor: help;"><path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" /><path d="M12 9h.01" /><path d="M11 12h1v4h1" /></svg>
          <span class="subscribe-form-tooltip" style="display: none; position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%); background: #1e293b; color: white; font-size: 11px; line-height: 1.4; padding: 6px 10px; border-radius: 6px; white-space: nowrap; z-index: 10; pointer-events: none;">이메일에는 포함되지 않으며, 비구독자에게만 표시됩니다</span>
        </span>
      </div>` : '';

    let contentHtml = '';

    if (layout === 'hero') {
      // Hero: big title + description + CTA centered
      const titleText = title || message || '뉴스레터를 받아보세요';
      const descText = description || '';
      contentHtml = `<div style="text-align: center;">
        <p style="font-size: 24px; font-weight: 700; margin: 0 0 8px 0;">${this._escape(titleText)}</p>
        ${descText ? `<p style="font-size: 15px; color: ${this._isDark() ? '#9ca3af' : '#64748b'}; margin: 0 0 16px 0;">${this._escape(descText)}</p>` : '<div style="margin-bottom: 16px;"></div>'}
        ${form_type === 'input'
          ? `<div style="display: flex; gap: 8px; justify-content: center; max-width: 400px; margin: 0 auto;">${inputHtml}${btnHtml}</div>`
          : btnHtml}
      </div>`;
    } else if (layout === 'inline') {
      // Inline: input + button only (no message)
      contentHtml = `<div style="display: flex; gap: 8px; align-items: center; justify-content: center; max-width: 400px; margin: 0 auto;">
        ${form_type === 'input' ? inputHtml : ''}${btnHtml}
      </div>`;
    } else {
      // Vertical (default): message + CTA
      contentHtml = `<div style="text-align: center;">
        <p style="font-size: 15px; margin: 0 0 12px 0;">${this._escape(message)}</p>
        ${form_type === 'input'
          ? `<div style="display: flex; gap: 8px; justify-content: center; max-width: 400px; margin: 0 auto;">${inputHtml}${btnHtml}</div>`
          : btnHtml}
      </div>`;
    }

    this.wrapper.innerHTML = contentHtml + hintHtml;

    const tooltipWrap = this.wrapper.querySelector(".subscribe-form-tooltip-wrap");
    if (tooltipWrap) {
      const tooltip = tooltipWrap.querySelector(".subscribe-form-tooltip");
      tooltipWrap.addEventListener("mouseenter", () => { tooltip.style.display = "block"; });
      tooltipWrap.addEventListener("mouseleave", () => { tooltip.style.display = "none"; });
    }
  }

  _renderEdit() {
    this.editing = true;
    this.wrapper.className = "ejs-widget ejs-widget--editing";
    this.wrapper.style.cssText = "";

    const { layout, form_type } = this.data;
    const isHero = layout === 'hero';

    this.wrapper.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 12px;">
        <!-- 레이아웃 선택 -->
        <div>
          <label class="ejs-widget__label">레이아웃</label>
          <div style="display: flex; gap: 4px;">
            <button type="button" data-layout="vertical" class="ejs-widget__btn ${layout === 'vertical' ? 'ejs-widget__btn--active' : ''}" style="padding: 3px 10px; font-size: 11px;">기본</button>
            <button type="button" data-layout="inline" class="ejs-widget__btn ${layout === 'inline' ? 'ejs-widget__btn--active' : ''}" style="padding: 3px 10px; font-size: 11px;">인라인</button>
            <button type="button" data-layout="hero" class="ejs-widget__btn ${layout === 'hero' ? 'ejs-widget__btn--active' : ''}" style="padding: 3px 10px; font-size: 11px;">히어로</button>
          </div>
        </div>

        <!-- 폼 타입 선택 -->
        <div>
          <label class="ejs-widget__label">구독 방식</label>
          <div style="display: flex; gap: 4px;">
            <button type="button" data-formtype="modal" class="ejs-widget__btn ${form_type === 'modal' ? 'ejs-widget__btn--active' : ''}" style="padding: 3px 10px; font-size: 11px;">버튼</button>
            <button type="button" data-formtype="input" class="ejs-widget__btn ${form_type === 'input' ? 'ejs-widget__btn--active' : ''}" style="padding: 3px 10px; font-size: 11px;">이메일 입력</button>
          </div>
        </div>

        <!-- 히어로 전용: 제목 + 설명 -->
        <div class="subscribe-form-hero-fields" style="${isHero ? '' : 'display: none;'}">
          <div style="margin-bottom: 8px;">
            <label class="ejs-widget__label">제목</label>
            <input type="text" data-field="title" value="${this._escape(this.data.title)}" placeholder="뉴스레터를 받아보세요" class="ejs-widget__input">
          </div>
          <div>
            <label class="ejs-widget__label">설명</label>
            <input type="text" data-field="description" value="${this._escape(this.data.description)}" placeholder="매주 새로운 소식을 전해드립니다" class="ejs-widget__input">
          </div>
        </div>

        <!-- 기본/인라인: 안내 메시지 -->
        <div class="subscribe-form-message-field" style="${isHero ? 'display: none;' : ''}">
          <label class="ejs-widget__label">안내 메시지</label>
          <input type="text" data-field="message" value="${this._escape(this.data.message)}" class="ejs-widget__input">
        </div>

        <!-- 공통: 버튼 이름 + 색상 -->
        <div>
          <div style="margin-bottom: 8px;">
            <label class="ejs-widget__label">버튼 이름</label>
            <input type="text" data-field="button_text" value="${this._escape(this.data.button_text)}" class="ejs-widget__input">
          </div>
          <div style="display: flex; gap: 8px; align-items: end;">
            <div>
              <label class="ejs-widget__label">글자색</label>
              <input type="color" data-field="button_text_color" value="${this.data.button_text_color}" class="ejs-widget__input" style="width: 42px; height: 34px; padding: 2px; cursor: pointer;">
            </div>
            <div>
              <label class="ejs-widget__label">배경색</label>
              <input type="color" data-field="button_background_color" value="${this.data.button_background_color}" class="ejs-widget__input" style="width: 42px; height: 34px; padding: 2px; cursor: pointer;">
            </div>
            <div class="subscribe-form-presets" style="display: flex; align-items: center;"></div>
            <div style="flex: 1; display: flex; align-items: center; justify-content: center;">
              <span class="subscribe-form-preview-btn" style="display: inline-block; padding: 6px 16px; background: ${this._escape(this.data.button_background_color)}; color: ${this._escape(this.data.button_text_color)}; border-radius: 6px; font-size: 13px; font-weight: 500;">${this._escape(this.data.button_text || "구독하기")}</span>
            </div>
          </div>
        </div>
      </div>
    `;

    const previewBtn = this.wrapper.querySelector(".subscribe-form-preview-btn");
    const textColorInput = this.wrapper.querySelector('[data-field="button_text_color"]');
    const bgColorInput = this.wrapper.querySelector('[data-field="button_background_color"]');
    const heroFields = this.wrapper.querySelector('.subscribe-form-hero-fields');
    const messageField = this.wrapper.querySelector('.subscribe-form-message-field');

    const updatePreview = () => {
      if (previewBtn) {
        previewBtn.style.background = this.data.button_background_color;
        previewBtn.style.color = this.data.button_text_color;
        previewBtn.textContent = this.data.button_text || "구독하기";
      }
    };

    // 레이아웃 버튼 이벤트
    this.wrapper.querySelectorAll("[data-layout]").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.data.layout = btn.dataset.layout;
        this.wrapper.querySelectorAll("[data-layout]").forEach(b => b.classList.remove("ejs-widget__btn--active"));
        btn.classList.add("ejs-widget__btn--active");
        // 히어로 필드 토글
        if (this.data.layout === 'hero') {
          heroFields.style.display = '';
          messageField.style.display = 'none';
        } else {
          heroFields.style.display = 'none';
          messageField.style.display = '';
        }
      });
    });

    // 폼 타입 버튼 이벤트
    this.wrapper.querySelectorAll("[data-formtype]").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.data.form_type = btn.dataset.formtype;
        this.wrapper.querySelectorAll("[data-formtype]").forEach(b => b.classList.remove("ejs-widget__btn--active"));
        btn.classList.add("ejs-widget__btn--active");
      });
    });

    // 입력 필드 이벤트
    this.wrapper.querySelectorAll("input").forEach(input => {
      input.addEventListener("input", () => {
        this.data[input.dataset.field] = input.value;
        updatePreview();
      });
      input.addEventListener("keydown", (e) => {
        stopPropagationOnEditorJsControlKeys(e);
        if (e.key === "Enter") { e.preventDefault(); this._renderView(); }
      });
    });

    // 프리셋 팔레트 (드롭다운)
    const presetsContainer = this.wrapper.querySelector(".subscribe-form-presets");
    const presetDropdown = createColorPresetDropdown((textColor, bgColor) => {
      this.data.button_text_color = textColor;
      this.data.button_background_color = bgColor;
      textColorInput.value = textColor;
      bgColorInput.value = bgColor;
      updatePreview();
    }, { text: this.config.defaultTextColor, bg: this.config.defaultBackgroundColor });
    presetsContainer.appendChild(presetDropdown);

    setTimeout(() => this.wrapper.querySelector("input[type=text]")?.focus(), 50);
  }

  save() {
    return {
      layout: this.data.layout,
      form_type: this.data.form_type,
      message: this.data.message,
      title: this.data.title,
      description: this.data.description,
      button_text: this.data.button_text,
      button_text_color: this.data.button_text_color,
      button_background_color: this.data.button_background_color,
    };
  }

  validate() { return true; }
  _escape(str) { const d = document.createElement("div"); d.textContent = str || ""; return d.innerHTML; }
}
